import { useState } from "react";
import { format, isValid } from "date-fns";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Badge } from "@components";
import Tooltip from "@components/Tooltip";
import { useEarlySettlementDiscount } from "@flags";
import { formatAmount, cn } from "@utils";
import { SINGLE_LINE_TOOLTIP } from "./documentActions";

// groupTransactions splits "future" by month; the design only distinguishes these three.
const BUCKETS = [
  { key: "overdue", label: "Overdue", dot: "bg-rose-400" },
  { key: "thisMonth", label: "Due this month", dot: "bg-emerald-400" },
  { key: "future", label: "Due in future", dot: "bg-zinc-300" },
];

function bucketFor(groupName) {
  if (groupName === "Overdue") return "overdue";
  if (groupName === "Due this month") return "thisMonth";
  return "future";
}

// The header chevron's column; invoice rows reserve the same width so amounts align.
const END_COLUMN = "flex w-4 shrink-0 items-center justify-end";

const entryAmount = (entry) => entry.amount || entry.remainingAmount || 0;

const entryTotal = (entry) =>
  entryAmount(entry) +
  (entry.associatedCreditNotes ?? []).reduce((sum, cn) => sum + entryAmount(cn), 0);

// Ledger numbers may already carry the prefix (e.g. "CN10421"); don't double it.
const creditNoteLabel = (number) => (/^cn/i.test(number ?? "") ? number : `CN ${number}`);

function plural(count, word) {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

function countLabel(entries) {
  const creditNotes = entries.filter((e) => e.type === "CreditNote").length;
  const invoices = entries.length - creditNotes;
  return [invoices && plural(invoices, "invoice"), creditNotes && plural(creditNotes, "credit note")]
    .filter(Boolean)
    .join(" · ");
}

function InvoiceRow({ entry, details, showDiscount, onOpenDetails }) {
  const isCreditNote = entry.type === "CreditNote";
  const remaining = entryAmount(entry);
  const due = new Date(entry.dueDate);
  const isPartial =
    !isCreditNote &&
    entry.initialAmount != null &&
    Math.abs(remaining) < Math.abs(entry.initialAmount) - 0.005;
  const subtitle = [
    isValid(due) && `Due ${format(due, "d MMM yyyy")}`,
    ...(entry.associatedCreditNotes ?? []).map(
      (cn) => `${creditNoteLabel(cn.transactionNumber)} attached · ${formatAmount(-Math.abs(entryAmount(cn)))}`,
    ),
  ]
    .filter(Boolean)
    .join(" · ");
  const discount = entry.discount?.evaluation;
  const saving =
    showDiscount && discount?.isApplicable
      ? Math.round((discount.applicableAmountUnrounded ?? 0) * 100) / 100
      : 0;

  return (
    <div
      {...(details && {
        role: "button",
        tabIndex: 0,
        "aria-label": `View details for ${entry.transactionNumber}`,
        onClick: () => onOpenDetails(details),
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpenDetails(details);
          }
        },
      })}
      className={cn(
        "group flex items-center gap-3 py-3 pl-9 pr-4 transition-colors",
        details && "cursor-pointer hover:bg-zinc-50 focus:outline-none focus-visible:bg-zinc-50",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium text-zinc-900">{entry.transactionNumber}</span>
          {entry.isUnderQuery && <Badge color="yellow">Under query</Badge>}
          {isPartial && (
            <Tooltip
              content={`${formatAmount(remaining)} remaining`}
              wrapperClassName="flex shrink-0"
              className={SINGLE_LINE_TOOLTIP}
            >
              <Badge color="yellow">Partially paid</Badge>
            </Tooltip>
          )}
        </div>
        {subtitle && <div className="mt-0.5 truncate text-xs text-zinc-500">{subtitle}</div>}
        {saving > 0 && (
          <div className="mt-0.5 text-xs text-green-700">
            Save {formatAmount(saving)}
            {entry.discount.terms?.expiresAt &&
              ` if paid by ${format(new Date(entry.discount.terms.expiresAt), "d MMM")}`}
          </div>
        )}
      </div>
      {/* Amount and the hover hint share one grid cell so hover swaps them without shifting the row. */}
      <div className="grid shrink-0 items-center justify-items-end">
        <span
          className={cn(
            "col-start-1 row-start-1 w-24 text-right text-sm font-medium tabular-nums text-zinc-900 transition-opacity",
            details && "group-hover:opacity-0 group-focus-visible:opacity-0",
          )}
        >
          {formatAmount(entryTotal(entry))}
        </span>
        {details && (
          <span
            aria-hidden="true"
            className={cn(
              "col-start-1 row-start-1 whitespace-nowrap text-sm font-medium text-zinc-600 opacity-0 transition-opacity",
              "group-hover:opacity-100 group-focus-visible:opacity-100",
            )}
          >
            Click to view
          </span>
        )}
      </div>
      <span aria-hidden="true" className={END_COLUMN} />
    </div>
  );
}

function BucketSection({ bucket, entries, open, onToggle, invoiceDetailsMap, ...rowProps }) {
  const isEmpty = entries.length === 0;
  const total = entries.reduce((sum, e) => sum + entryTotal(e), 0);

  return (
    <div>
      <div
        role="button"
        tabIndex={isEmpty ? -1 : 0}
        aria-expanded={open}
        aria-disabled={isEmpty}
        onClick={() => !isEmpty && onToggle()}
        onKeyDown={(e) => {
          if (!isEmpty && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onToggle();
          }
        }}
        className={cn(
          "flex items-center gap-3 px-4 py-3.5 transition-colors",
          isEmpty ? "cursor-default" : "cursor-pointer hover:bg-zinc-50",
          open && "bg-zinc-50/60",
        )}
      >
        <span aria-hidden="true" className={cn("size-1.5 shrink-0 rounded-full", bucket.dot)} />
        <span className="text-sm font-medium text-zinc-900">{bucket.label}</span>
        {!isEmpty && <span className="text-xs text-zinc-500">{countLabel(entries)}</span>}
        <span
          className={cn(
            "ml-auto text-sm font-medium tabular-nums",
            isEmpty ? "text-zinc-400" : "text-zinc-900",
          )}
        >
          {formatAmount(total)}
        </span>
        <span className={cn(END_COLUMN, "justify-end")}>
          <ChevronDownIcon
            aria-hidden="true"
            className={cn(
              "size-4 shrink-0 transition-transform",
              isEmpty ? "text-zinc-300" : "text-zinc-500",
              open && "rotate-180",
            )}
          />
        </span>
      </div>
      {open && !isEmpty && (
        <div className="divide-y divide-zinc-100 border-t border-zinc-100">
          {entries.map((entry) => (
            <InvoiceRow
              key={entry.transactionNumber}
              entry={entry}
              details={invoiceDetailsMap[entry.transactionNumber]}
              {...rowProps}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Balance-tab bucket breakdown: open invoices (and unmatched credit notes) grouped into Overdue / Due this
// month / Due in future, each expandable to its invoices.
export default function BalanceInvoiceList({ groups, invoiceDetailsMap, expandAll, onOpenDetails }) {
  const showDiscount = useEarlySettlementDiscount();
  const [openKeys, setOpenKeys] = useState(() => new Set());

  const entriesByBucket = { overdue: [], thisMonth: [], future: [] };
  for (const group of groups ?? []) entriesByBucket[bucketFor(group.name)].push(...group.invoices);

  const toggle = (key) =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white">
      <div className="divide-y divide-zinc-100">
        {BUCKETS.map((bucket) => {
          const entries = entriesByBucket[bucket.key];
          return (
            <BucketSection
              key={bucket.key}
              bucket={bucket}
              entries={entries}
              open={expandAll ? entries.length > 0 : openKeys.has(bucket.key)}
              onToggle={() => toggle(bucket.key)}
              showDiscount={showDiscount}
              onOpenDetails={onOpenDetails}
              invoiceDetailsMap={invoiceDetailsMap ?? {}}
            />
          );
        })}
      </div>
    </div>
  );
}
